import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SiteDocState, discardSiteDraft, getSiteDoc, publishSiteDoc, saveSiteDraft,
} from '../../../api/platform';
import { ApiRequestError } from '../../../api/client';
import { SiteDoc, changedPaths, resolveDoc } from '../../../shared/siteContent';
import { invalidateSiteContent } from '../../site/useSiteContent';

/**
 * The Site tab's document: what is live, the working copy on screen, and the
 * Super Admins' SHARED draft on the server (078).
 *
 * Every edit changes the working copy at once and is saved as the draft
 * ~800ms after the typing stops, one save at a time, each echoing the stamp
 * the last one returned. A save the server refuses with `draft_conflict`
 * (somebody else saved since) stops the autosave and raises `conflict`: the
 * page offers their draft or keeping this one, and nothing is overwritten
 * until somebody chooses. Publish sends the working copy itself — so a
 * keystroke still waiting for its save is published too — after any save in
 * flight has landed; Discard drops the draft the same way.
 */
const SAVE_AFTER_MS = 800;

export interface SiteConflict {
  /** The draft that is there now (null: they published or discarded it). */
  theirs: SiteDoc | null;
  published: SiteDoc;
  at: string | null;
  by: string | null;
}

export type Favicons = SiteDocState['favicons'];

export interface SiteDraft {
  loaded: boolean;
  loadError: string;
  published: SiteDoc | null;
  /** The working copy — what the fields and the preview show. */
  doc: SiteDoc | null;
  /** Every leaf that differs from what is live: the bar's count. */
  changes: string[];
  /** The stored draft's facts, for "Draft by Ben R. · 2h ago". */
  draftAt: string | null;
  draftBy: string | null;
  /** Something was typed in this visit and is not live — what the leave guard asks about. */
  editedHere: boolean;
  saving: boolean;
  publishing: boolean;
  /** A refused publish or save, in the server's words. */
  error: string;
  conflict: SiteConflict | null;
  favicons: Favicons;
  edit: (change: (d: SiteDoc) => void) => void;
  noteFavicon: (name: string, facts: Favicons[string]) => void;
  publish: () => Promise<void>;
  discard: () => Promise<void>;
  takeTheirs: () => void;
  keepMine: () => void;
  clearError: () => void;
}

const clone = (d: SiteDoc): SiteDoc => JSON.parse(JSON.stringify(d));

/** The 409's body, if the refusal is a draft conflict. */
function conflictOf(e: unknown): SiteConflict | null {
  if (!(e instanceof ApiRequestError) || e.status !== 409) return null;
  const b = (e.body || {}) as Partial<SiteDocState> & { code?: string };
  if (b.code !== 'draft_conflict' || !b.published) return null;
  return {
    theirs: b.draft ? resolveDoc(b.draft, resolveDoc(b.published)) : null,
    published: resolveDoc(b.published),
    at: b.draftAt ?? null,
    by: b.draftBy ?? null,
  };
}

export function useSiteDraft(onPublished: (message: string) => void): SiteDraft {
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [published, setPublished] = useState<SiteDoc | null>(null);
  const [doc, setDoc] = useState<SiteDoc | null>(null);
  const [draftAt, setDraftAt] = useState<string | null>(null);
  const [draftBy, setDraftBy] = useState<string | null>(null);
  const [editedHere, setEditedHere] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<SiteConflict | null>(null);
  const [favicons, setFavicons] = useState<Favicons>({});

  // The save machinery lives in refs: a timer's callback and a save's
  // continuation must see the newest copy and stamp, not a render's.
  const docRef = useRef<SiteDoc | null>(null);
  const baseRef = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflight = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const blocked = useRef(false);
  const alive = useRef(true);
  // Set again on mount, not only cleared on unmount: StrictMode mounts twice
  // in development, and a flag left false would drop every answer after.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    getSiteDoc()
      .then((r) => {
        if (!alive.current) return;
        const live = resolveDoc(r.published);
        const working = r.draft ? resolveDoc(r.draft, live) : clone(live);
        setPublished(live);
        setDoc(working);
        docRef.current = working;
        baseRef.current = r.draftAt;
        setDraftAt(r.draftAt);
        setDraftBy(r.draftBy);
        setFavicons(r.favicons || {});
        setLoaded(true);
      })
      .catch((e) => { if (alive.current) setLoadError(e.message || 'Could not load the site.'); });
  }, []);

  const save = useCallback((): Promise<void> => {
    if (blocked.current || !docRef.current) return Promise.resolve();
    if (inflight.current) { again.current = true; return inflight.current; }
    const sending = docRef.current;
    setSaving(true);
    const run = saveSiteDraft(sending, baseRef.current)
      .then((r) => {
        baseRef.current = r.draftAt;
        if (!alive.current) return;
        setDraftAt(r.draftAt);
        setDraftBy(r.draftBy);
      })
      .catch((e) => {
        const c = conflictOf(e);
        if (c) { blocked.current = true; if (alive.current) setConflict(c); return; }
        if (alive.current) setError(e.message || 'The draft could not be saved.');
      })
      .finally(() => {
        inflight.current = null;
        if (again.current && !blocked.current) { again.current = false; void save(); return; }
        again.current = false;
        if (alive.current) setSaving(false);
      });
    inflight.current = run;
    return run;
  }, []);

  /** Send what is waiting now, and wait for every save to land. */
  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; await save(); }
    while (inflight.current) await inflight.current;
  }, [save]);

  // Leaving the page (a link, the sidebar) keeps the last keystrokes: the
  // save that was waiting goes now rather than never.
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; void save(); }
  }, [save]);

  const edit = useCallback((change: (d: SiteDoc) => void) => {
    if (!docRef.current) return;
    const next = clone(docRef.current);
    change(next);
    docRef.current = next;
    setDoc(next);
    setEditedHere(true);
    setError('');
    if (blocked.current) return;
    setSaving(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void save(); }, SAVE_AFTER_MS);
  }, [save]);

  const publish = useCallback(async () => {
    if (!docRef.current) return;
    setPublishing(true);
    setError('');
    try {
      await flush();
      // A conflict is on screen: nothing goes live until somebody chooses —
      // and a caller waiting on this (the leave guard's "Publish and leave")
      // must hear that it did not happen, or it would leave as if it had.
      if (blocked.current) throw new Error('Somebody else changed the draft — choose theirs or yours first.');
      const r = await publishSiteDoc(docRef.current!, baseRef.current);
      const live = resolveDoc(r.published);
      baseRef.current = null;
      docRef.current = clone(live);
      invalidateSiteContent();
      if (!alive.current) return;
      setPublished(live);
      setDoc(docRef.current);
      setDraftAt(null);
      setDraftBy(null);
      setEditedHere(false);
      onPublished('Published · live on the public site now.');
    } catch (e) {
      const c = conflictOf(e);
      if (c) { blocked.current = true; setConflict(c); throw e; }
      setError((e as Error).message || 'The site could not be published.');
      throw e;
    } finally {
      if (alive.current) setPublishing(false);
    }
  }, [flush, onPublished]);

  const discard = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    while (inflight.current) await inflight.current;
    if (blocked.current || !published) return;
    try {
      await discardSiteDraft(baseRef.current);
      baseRef.current = null;
      docRef.current = clone(published);
      setDoc(docRef.current);
      setDraftAt(null);
      setDraftBy(null);
      setEditedHere(false);
      setSaving(false);
    } catch (e) {
      const c = conflictOf(e);
      if (c) { blocked.current = true; setConflict(c); return; }
      setError((e as Error).message || 'The draft could not be discarded.');
    }
  }, [published]);

  /** Load the draft that is there now (or, if they published it, what is live). */
  const takeTheirs = useCallback(() => {
    if (!conflict) return;
    const working = conflict.theirs ? clone(conflict.theirs) : clone(conflict.published);
    blocked.current = false;
    baseRef.current = conflict.at;
    docRef.current = working;
    setPublished(conflict.published);
    setDoc(working);
    setDraftAt(conflict.at);
    setDraftBy(conflict.by);
    setEditedHere(false);
    setSaving(false);
    setConflict(null);
  }, [conflict]);

  /** Save this copy over theirs, now that somebody has chosen to. */
  const keepMine = useCallback(() => {
    if (!conflict) return;
    blocked.current = false;
    baseRef.current = conflict.at;
    setPublished(conflict.published);
    setConflict(null);
    void save();
  }, [conflict, save]);

  const noteFavicon = useCallback((name: string, facts: Favicons[string]) => {
    setFavicons((f) => ({ ...f, [name]: facts }));
  }, []);

  return {
    loaded, loadError, published, doc,
    changes: doc && published ? changedPaths(published, doc) : [],
    draftAt, draftBy, editedHere, saving, publishing, error, conflict, favicons,
    edit, noteFavicon, publish, discard, takeTheirs, keepMine, clearError: () => setError(''),
  };
}
